import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Flag, Volume2, VolumeX, Play, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { KidAvatar } from "./Brand";
import { Thumb } from "./VideoCard";
import { kidApi, streamUrl, errMsg, KID_KEY } from "../lib/api";
import { catById } from "../lib/constants";

const RailBtn = ({ id, onClick, label, children, active }) => (
  <button data-testid={id} onClick={onClick} aria-label={label} className="flex flex-col items-center gap-1 text-white font-display text-xs font-semibold drop-shadow">
    <span className={`w-12 h-12 grid place-items-center rounded-full border-[3px] border-[#1B1A2E] shadow-[3px_3px_0_0_#1B1A2E] active:scale-90 transition-transform ${active ? "bg-[#FF8FC7]" : "bg-white"} text-[#1B1A2E]`}>{children}</span>
    {label}
  </button>
);

export const ShortSlide = ({ video, index, active, near, muted, setMuted, onEnded, onReport, kidId, saved, onToggleSave }) => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [cheers, setCheers] = useState({ count: video.cheers?.length || 0, cheered: video.cheers?.includes(kidId) });
  const cat = catById(video.category);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (!active) { v.pause(); return; }
    v.currentTime = 0;
    setPaused(false);
    v.play().catch(() => { v.muted = true; setMuted(true); v.play().catch(() => setPaused(true)); });
  }, [active, setMuted]);

  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPaused(false); } else { v.pause(); setPaused(true); }
  };

  const cheer = async () => {
    try {
      const { data } = await kidApi.post(`/kid/videos/${video.id}/cheer`);
      setCheers({ count: data.cheer_count, cheered: data.cheered });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section data-index={index} data-testid={`short-${video.id}`} className="relative h-[100svh] w-full snap-start snap-always overflow-hidden bg-[#1B1A2E]">
      <div className="absolute inset-0 zn-dots opacity-30" style={{ backgroundColor: cat.color }} />
      {video.thumb && <Thumb id={video.id} title={video.title} className="scale-110 blur-2xl !opacity-50" />}
      {near && (
        <video
          ref={ref}
          data-testid={active ? "short-active-video" : undefined}
          src={streamUrl(video.id, localStorage.getItem(KID_KEY))}
          muted={muted}
          playsInline
          preload={active ? "auto" : "metadata"}
          controlsList="nodownload"
          disablePictureInPicture
          onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime / (e.currentTarget.duration || video.duration || 10))}
          onEnded={onEnded}
          onClick={togglePlay}
          className="absolute inset-0 w-full h-full object-contain"
        />
      )}
      {paused && active && (
        <button data-testid="short-play-btn" onClick={togglePlay} className="absolute inset-0 m-auto w-20 h-20 rounded-full bg-white/90 border-[3px] border-[#1B1A2E] grid place-items-center" aria-label="Play">
          <Play size={34} fill="#1B1A2E" />
        </button>
      )}
      <div className="absolute top-[calc(env(safe-area-inset-top)+4.25rem)] inset-x-4 h-1.5 rounded-full bg-white/30 overflow-hidden">
        <div data-testid={active ? "short-progress" : undefined} className="h-full bg-[#FFD23F]" style={{ width: `${active ? Math.min(100, progress * 100) : 0}%` }} />
      </div>
      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
      <div className="absolute left-4 right-24 bottom-[calc(env(safe-area-inset-bottom)+1.5rem)] text-white">
        <Link to={`/kid/creator/${video.kid_id}`} data-testid={`short-creator-${video.id}`} className="inline-flex items-center gap-2 mb-2">
          <KidAvatar avatar={video.author?.avatar} color={video.author?.color} frame={video.author?.frame} size={38} />
          <span className="font-display font-semibold text-lg drop-shadow">{video.author?.name}</span>
        </Link>
        <h2 className="font-display text-2xl font-bold leading-tight drop-shadow break-words">{video.title}</h2>
        <span className="zn-chip !py-1 !min-h-0 mt-2 text-xs text-[#1B1A2E]" style={{ background: cat.color }}><cat.icon size={13} /> {cat.label}</span>
      </div>
      <div className="absolute right-3 bottom-[calc(env(safe-area-inset-bottom)+1.5rem)] flex flex-col gap-4">
        <RailBtn id={`short-cheer-${video.id}`} onClick={cheer} label={String(cheers.count)} active={cheers.cheered}><Heart size={22} fill={cheers.cheered ? "#1B1A2E" : "none"} /></RailBtn>
        <RailBtn id={`short-save-${video.id}`} onClick={() => onToggleSave(video.id)} label={saved ? "Saved" : "Save"} active={saved}><Bookmark size={22} fill={saved ? "#1B1A2E" : "none"} /></RailBtn>
        <Link to={`/kid/watch/${video.id}`} data-testid={`short-comments-${video.id}`} className="flex flex-col items-center gap-1 text-white font-display text-xs font-semibold drop-shadow">
          <span className="w-12 h-12 grid place-items-center rounded-full bg-white text-[#1B1A2E] border-[3px] border-[#1B1A2E] shadow-[3px_3px_0_0_#1B1A2E]"><MessageCircle size={22} /></span>
          Chat
        </Link>
        {video.kid_id !== kidId && <RailBtn id={`short-report-${video.id}`} onClick={() => onReport(video.id)} label="Report"><Flag size={20} /></RailBtn>}
        <RailBtn id={`short-mute-${video.id}`} onClick={() => setMuted(!muted)} label={muted ? "Sound" : "Mute"}>{muted ? <VolumeX size={22} /> : <Volume2 size={22} />}</RailBtn>
      </div>
    </section>
  );
};
